import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ExternalLink, Calendar, Newspaper, MapPin, User, Loader2, Quote } from 'lucide-react';
import NewsletterSignup from '../components/common/NewsletterSignup';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback static articles (will be replaced by API data)
const fallbackArticles = [
  {
    id: 'fallback-1',
    headline: "Nimoy Knight Foundation presents 'Live Long & Prosper' Tribute Award",
    source: "Long Island Press",
    pub_date: "October 16, 2025",
    summary: "The Nimoy Knight Foundation announced that Tim Howe, chief operating officer of Spectrum Designs Foundation, and Temple Grandin, world-renowned autism advocate and animal science professor, have been awarded the 'Live Long & Prosper' Tribute Award.",
    external_link: "https://www.longislandpress.com/2025/10/16/nimoy-knight-foundation/",
    image_url: "https://www.longislandpress.com/wp-content/uploads/2025/10/Screenshot-2025-10-16-134616.png",
    is_featured: true
  },
  {
    id: 'fallback-2',
    headline: "Film Documenting Leonard Nimoy's Battle with COPD Gets Second Release",
    source: "Respiratory Therapy",
    pub_date: "February 28, 2025",
    summary: "Ten years after Leonard Nimoy's death from COPD, a documentary covering the actor's struggle with the disease is being re-released via a partnership by The COPD Foundation, The Nimoy Knight Foundation, and Health Point Productions.",
    external_link: "https://respiratory-therapy.com/disorders-diseases/chronic-pulmonary-disorders/copd/film-leonard-nimoy-copd-rerelease/",
    image_url: null,
    is_featured: false
  }
];

const InTheNewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/news`);
        if (response.data && response.data.length > 0) {
          setArticles(response.data);
        } else {
          setArticles(fallbackArticles);
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        setArticles(fallbackArticles);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const featuredArticle = articles.find(a => a.is_featured) || articles[0];
  const otherArticles = articles.filter(a => a.id !== featuredArticle?.id);

  const renderArticleContent = (article) => {
    if (!article.body_content) return null;
    
    // Split by double newlines for paragraphs
    const paragraphs = article.body_content.split('\n\n').filter(p => p.trim());
    
    return paragraphs.map((para, idx) => {
      // Check if it looks like a quote (starts with ")
      if (para.trim().startsWith('"') || para.trim().startsWith('"')) {
        return (
          <blockquote key={idx} className="border-l-4 border-amber-500 pl-6 my-6 italic">
            <p className="text-gray-300 text-lg leading-relaxed">{para}</p>
          </blockquote>
        );
      }
      // Check if it's a subheadline (short and might be bold indicator)
      if (para.length < 80 && !para.includes('.')) {
        return (
          <h3 key={idx} className="text-xl font-bold text-white mt-8 mb-4">{para}</h3>
        );
      }
      return (
        <p key={idx} className="text-gray-400 leading-relaxed mb-4">{para}</p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 rounded-xl mb-6">
            <Newspaper className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">In The </span>
            <span className="text-amber-500">News</span>
          </h1>
          <p className="text-gray-300 text-lg">
            Latest news, press releases, and media coverage about the Nimoy-Knight Foundation and our mission to honor Leonard Nimoy's legacy.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Featured Article */}
          {featuredArticle && (
            <section className="py-8 bg-[#0a0a0b]">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                  onClick={() => featuredArticle.body_content ? setSelectedArticle(featuredArticle) : window.open(featuredArticle.external_link, '_blank')}
                  className="group cursor-pointer bg-[#141416] border border-gray-800/50 rounded-3xl overflow-hidden hover:border-amber-500/50 transition-all"
                >
                  <div className="flex flex-col lg:flex-row">
                    {/* Image */}
                    {featuredArticle.image_url && (
                      <div className="lg:w-2/5 aspect-video lg:aspect-auto">
                        <img
                          src={featuredArticle.image_url}
                          alt={featuredArticle.headline}
                          className="w-full h-full object-cover"
                          loading="eager"
                        />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className={`${featuredArticle.image_url ? 'lg:w-3/5' : 'w-full'} p-8 lg:p-10 flex flex-col justify-center`}>
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="px-3 py-1 bg-amber-500/10 text-amber-500 text-sm font-semibold rounded-full">
                          Featured
                        </span>
                        {featuredArticle.source && (
                          <span className="text-amber-500 text-sm font-medium">
                            {featuredArticle.source}
                          </span>
                        )}
                      </div>
                      
                      <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-amber-500 transition-colors">
                        {featuredArticle.headline}
                      </h2>

                      {featuredArticle.subheadline && (
                        <p className="text-amber-500/80 text-lg mb-4">{featuredArticle.subheadline}</p>
                      )}
                      
                      <p className="text-gray-400 mb-6 line-clamp-3">
                        {featuredArticle.summary || featuredArticle.body_content?.substring(0, 250) + '...'}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        {featuredArticle.pub_date && (
                          <span className="text-gray-500 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {featuredArticle.pub_date}
                          </span>
                        )}
                        {featuredArticle.location && (
                          <span className="text-gray-500 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {featuredArticle.location}
                          </span>
                        )}
                        {featuredArticle.author && (
                          <span className="text-gray-500 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            {featuredArticle.author}
                          </span>
                        )}
                        <span className="flex items-center gap-2 text-amber-500 font-medium group-hover:gap-3 transition-all ml-auto">
                          {featuredArticle.body_content ? 'Read More' : 'Read Full Article'}
                          <ExternalLink className="w-5 h-5" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Other News Articles */}
          {otherArticles.length > 0 && (
            <section className="py-12 bg-[#0a0a0b]">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-2xl font-bold text-white mb-8">More Coverage</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {otherArticles.map((article) => (
                    <div
                      key={article.id}
                      onClick={() => article.body_content ? setSelectedArticle(article) : window.open(article.external_link, '_blank')}
                      className="group cursor-pointer bg-[#141416] border border-gray-800/50 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all"
                    >
                      {article.image_url && (
                        <div className="aspect-video">
                          <img
                            src={article.image_url}
                            alt={article.headline}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        {/* Source & Date */}
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-amber-500 text-sm font-medium">
                            {article.source || 'Press Release'}
                          </span>
                          {article.pub_date && (
                            <span className="text-gray-500 text-sm flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {article.pub_date}
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-amber-500 transition-colors line-clamp-2">
                          {article.headline}
                        </h3>

                        {article.subheadline && (
                          <p className="text-amber-500/70 text-sm mb-2">{article.subheadline}</p>
                        )}

                        {/* Description */}
                        <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                          {article.summary || article.body_content?.substring(0, 150) + '...'}
                        </p>

                        {/* Meta info */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {article.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {article.location}
                              </span>
                            )}
                          </div>
                          <span className="flex items-center gap-1 text-amber-500 text-sm font-medium group-hover:gap-2 transition-all">
                            Read
                            <ExternalLink className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {articles.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500">No news articles available at this time.</p>
            </div>
          )}
        </>
      )}

      {/* Article Modal */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-8 px-4"
          onClick={() => setSelectedArticle(null)}
        >
          <div 
            className="bg-[#141416] border border-gray-800 rounded-3xl max-w-4xl w-full my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Image */}
            {selectedArticle.image_url && (
              <div className="aspect-video rounded-t-3xl overflow-hidden">
                <img
                  src={selectedArticle.image_url}
                  alt={selectedArticle.headline}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-8 md:p-12">
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
                {selectedArticle.source && (
                  <span className="px-3 py-1 bg-amber-500/10 text-amber-500 rounded-full font-medium">
                    {selectedArticle.source}
                  </span>
                )}
                {selectedArticle.pub_date && (
                  <span className="text-gray-500 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {selectedArticle.pub_date}
                  </span>
                )}
                {selectedArticle.location && (
                  <span className="text-gray-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {selectedArticle.location}
                  </span>
                )}
              </div>

              {/* Headline */}
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {selectedArticle.headline}
              </h1>

              {selectedArticle.subheadline && (
                <p className="text-xl text-amber-500 mb-6">{selectedArticle.subheadline}</p>
              )}

              {selectedArticle.author && (
                <p className="text-gray-500 mb-8 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  By {selectedArticle.author}
                </p>
              )}

              {/* Body Content */}
              <div className="prose prose-invert max-w-none">
                {selectedArticle.summary && (
                  <p className="text-lg text-gray-300 leading-relaxed mb-6 font-medium">
                    {selectedArticle.summary}
                  </p>
                )}
                {renderArticleContent(selectedArticle)}
              </div>

              {/* External Link */}
              {selectedArticle.external_link && (
                <div className="mt-8 pt-8 border-t border-gray-800">
                  <a
                    href={selectedArticle.external_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all"
                  >
                    Read Original Article
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => setSelectedArticle(null)}
                className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/80 text-white rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Signup */}
      <NewsletterSignup />

      {/* CTA Section */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Media Inquiries
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            For press inquiries, interview requests, or media coverage opportunities, please contact us.
          </p>
          <a
            href="mailto:info@rememberingleonardnimoy.org"
            className="inline-flex items-center justify-center px-8 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105"
          >
            Contact Media Relations
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InTheNewsPage;
